<!--
  Модалка выбора типа ЭДО-документа.
  Показывает доступные сценарии создания и отдаёт выбранный тип родительской странице.
-->
<template>
  <BaseModal
    :visible="true"
    title="Создать ЭДО-документ"
    size="lg"
    class="create-document-modal"
    @close="close"
  >
    <div class="create-document-modal__stepper">
      <div
        v-for="item in visibleSteps"
        :key="item.key"
        class="create-document-modal__step"
        :class="stepClass(item.key)"
      >
        <span class="create-document-modal__step-index">
          {{ item.number }}
        </span>
        <span class="create-document-modal__step-text">
          <span class="create-document-modal__step-title">{{ item.title }}</span>
          <span class="create-document-modal__step-description">{{ item.description }}</span>
        </span>
      </div>
    </div>

    <p class="create-document-modal__subtitle">
      {{ stepSubtitle }}
    </p>

    <div v-if="step === 'integration'" class="create-options">
      <div
        v-if="integrationRecommendationHint"
        class="create-document-modal__hint"
      >
        <AppIcon
          class="create-document-modal__hint-icon"
          name="info"
          :size="16"
        />
        <span>
          <strong>Рекомендуем использовать «{{ recommendedIntegrationTitle }}»</strong>
          <span>{{ integrationRecommendationHint }}</span>
        </span>
      </div>
      <div
        v-if="integrationInvalid"
        class="create-document-modal__error"
      >
        Выберите интеграцию Saby
      </div>
      <button
        v-for="integration in integrationOptions"
        :key="integration.id"
        class="create-option"
        :class="{
          'create-option--selected': String(selectedIntegrationId) === String(integration.id),
        }"
        type="button"
        @click="selectIntegrationOption(integration.id)"
      >
        <span class="create-option__content">
          <span class="create-option__title-row">
            <span class="create-option__title">{{ integration.title }}</span>
            <span
              v-if="integration.recommended"
              class="create-option__badge"
            >
              Рекомендуется
            </span>
          </span>
          <span class="create-option__description">
            {{ integration.description }}
          </span>
        </span>
        <span class="create-option__action">
          {{ integrationActionLabel(integration.id) }}
        </span>
      </button>
      <div
        v-if="!integrationStore.dataIsLoading && !integrationOptions.length"
        class="create-document-modal__empty"
      >
        Нет включённых интеграций Saby
      </div>
    </div>

    <div v-if="step === 'document'" class="create-options">
      <button
        v-for="option in options"
        :key="option.key"
        class="create-option"
        :class="{
          'create-option--disabled': option.disabled,
        }"
        type="button"
        :disabled="option.disabled"
        @click="selectDocument(option)"
      >
        <span class="create-option__content">
          <span class="create-option__title">{{ option.title }}</span>
          <span class="create-option__description">
            {{ option.description }}
          </span>
        </span>
        <span class="create-option__action">
          {{ option.disabled ? "Скоро" : "Выбрать" }}
        </span>
      </button>
    </div>

    <div v-else-if="step === 'source'" class="create-options">
      <button
        v-for="source in sourceOptions"
        :key="source.key"
        class="create-option"
        :class="{ 'create-option--disabled': source.disabled }"
        type="button"
        :disabled="source.disabled"
        @click="selectSource(source)"
      >
        <span class="create-option__content">
          <span class="create-option__title">{{ source.title }}</span>
          <span class="create-option__description">
            {{ source.description }}
          </span>
        </span>
        <span class="create-option__action">
          {{ source.disabled ? "Недоступно" : "Продолжить" }}
        </span>
      </button>
    </div>

    <div v-else-if="step === 'file'" class="create-document-modal__file-step">
      <input
        ref="xmlFileInput"
        class="create-document-modal__file-input"
        type="file"
        accept=".xml,application/xml,text/xml"
        @change="handleXmlFileChange"
      >

      <button
        class="create-option create-option--file"
        :class="{ 'create-option--selected': xmlFile }"
        type="button"
        @click="openXmlFilePicker"
      >
        <span class="create-option__content">
          <span class="create-option__title">
            {{ xmlFile ? xmlFile.name : "Выберите XML-файл" }}
          </span>
          <span class="create-option__description">
            {{ xmlFileDescription }}
          </span>
        </span>
        <span class="create-option__action">
          {{ xmlFile ? "Заменить" : "Выбрать" }}
        </span>
      </button>

      <div
        v-if="xmlFileInvalid"
        class="create-document-modal__error"
      >
        Выберите XML-файл
      </div>
    </div>

    <template #footer>
      <base-button
        v-if="canGoBack"
        type="button"
        :disabled="isSubmitting"
        @click="goBack"
      >
        Назад
      </base-button>
      <base-button
        type="button"
        :disabled="isSubmitting"
        @click="close"
      >
        Отмена
      </base-button>
      <base-primary-button
        v-if="step === 'file'"
        type="button"
        :disabled="isSubmitting"
        @click="submitXmlFile"
      >
        {{ isSubmitting ? "Создание..." : "Создать черновик" }}
      </base-primary-button>
    </template>
  </BaseModal>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import { useIntegrationStore } from "@/stores/integrationStore";

export default {
  name: "EdoCreateDocumentModal",

  components: {
    AppIcon,
  },

  props: {
    initialIntegrationId: {
      type: [String, Number],
      default: null,
    },
    sourceOrderId: {
      type: [String, Number],
      default: null,
    },
    sourceOrganizationInn: {
      type: [String, Number],
      default: null,
    },
    initialDocumentType: {
      type: String,
      default: null,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["close", "select"],

  data() {
    return {
      step: this.initialDocumentType ? "source" : "document",
      selectedDocumentType: this.initialDocumentType,
      selectedIntegrationId: this.initialIntegrationId,
      selectedSourceType: null,
      integrationInvalid: false,
      xmlFile: null,
      xmlFileBase64: "",
      xmlFileInvalid: false,
      steps: [
        {
          key: "document",
          number: 1,
          title: "Документ",
          description: "Тип ЭДО",
        },
        {
          key: "source",
          number: 2,
          title: "Источник",
          description: "Данные",
        },
        {
          key: "integration",
          number: 3,
          title: "Интеграция",
          description: "Кабинет Saby",
        },
        {
          key: "file",
          number: 4,
          title: "Файл",
          description: "XML",
        },
      ],
      options: [
        {
          key: "transport_order",
          title: "Заказ-заявка",
          description:
            "ЭЗЗ фиксирует договорённость по перевозке: участники, маршрут, груз и условия заявки",
        },
        {
          key: "consignment_note",
          title: "ЭТрН",
          description:
            "Электронная транспортная накладная оформляет перевозку после согласования заказ-заявки",
        },
        {
          key: "forwarder_order",
          title: "Поручение экспедитору",
          description:
            "Клиент поручает экспедитору организовать перевозку и передаёт условия экспедирования",
        },
        {
          key: "forwarder_receipt",
          title: "Экспедиторская расписка",
          description:
            "Экспедитор подтверждает получение груза от клиента или указанного грузоотправителя",
          disabled: true,
        },
      ],
    };
  },

  computed: {
    visibleSteps() {
      return this.steps.filter((item) => item.key !== "file" || this.selectedSourceType === "xml");
    },

    canGoBack() {
      if (this.initialDocumentType && this.step === "source") return false;

      return this.step !== "document";
    },

    integrationStore() {
      return useIntegrationStore();
    },

    integrationOptions() {
      return this.integrationStore.sabyIntegrations
        .filter((integration) => integration.enabled)
        .map((integration) => ({
          id: integration.id,
          title: this.integrationTitle(integration),
          description: this.integrationDescription(integration),
          recommended:
            !!this.recommendedIntegration &&
            String(integration.id) === String(this.recommendedIntegration.id),
        }));
    },

    sourceIntegration() {
      if (!this.sourceOrganizationInn) return null;

      const sourceInn = String(this.sourceOrganizationInn).trim();
      return this.integrationStore.sabyIntegrations.find(
        (integration) =>
          integration.enabled &&
          String(integration.org_inn || "").trim() === sourceInn
      ) || null;
    },

    recommendedIntegration() {
      if (this.selectedSourceType !== "order") return null;

      return this.sourceIntegration;
    },

    recommendedIntegrationTitle() {
      if (!this.recommendedIntegration) return "";

      return this.integrationRecommendationTitle(this.recommendedIntegration);
    },

    selectedDocument() {
      return this.options.find((option) => option.key === this.selectedDocumentType);
    },

    stepSubtitle() {
      if (this.step === "file") {
        return `Документ: ${this.selectedDocument?.title || "ЭДО-документ"}. Загрузите XML, чтобы создать редактируемый черновик`;
      }

      if (this.step === "integration") {
        return `Документ: ${this.selectedDocument?.title || "ЭДО-документ"}. Выберите кабинет Saby`;
      }

      if (this.step === "source") {
        return `Документ: ${this.selectedDocument?.title || "ЭДО-документ"}. Выберите источник данных`;
      }

      return "Выберите документ, который нужно подготовить";
    },

    xmlFileDescription() {
      if (!this.xmlFile) {
        return "Мы распарсим XML и откроем обычный черновик, чтобы его можно было проверить и дополнить";
      }

      return `${this.formatFileSize(this.xmlFile.size)} · будет создан черновик из данных файла`;
    },

    integrationRecommendationHint() {
      if (!this.recommendedIntegration) return "";

      return "Этот контрагент указан как грузоотправитель, поэтому рекомендуем использовать эту интеграцию";
    },

    sourceOptions() {
      return [
        {
          key: "order",
          title: "Из текущего заказа",
          description: this.sourceOrderId
            ? "Перенесём контрагентов, маршрут, груз и связанные рейсовые данные"
            : "Доступно при создании ЭДО из карточки заказа",
          disabled: !this.sourceOrderId,
        },
        {
          key: "empty",
          title: "Пустой документ",
          description:
            "Откроем форму без автозаполнения, данные можно внести вручную",
        },
        {
          key: "xml",
          title: "На основе XML",
          description:
            "Загрузим XML, распарсим данные и создадим черновик для проверки",
        },
      ];
    },
  },

  async created() {
    if (!this.integrationStore.integrations.length) {
      await this.integrationStore.fetchIntegrations();
    }
  },

  methods: {
    close() {
      this.$emit("close");
    },

    selectDocument(option) {
      if (option.disabled) return;

      this.selectedDocumentType = option.key;
      this.step = "source";
    },

    selectIntegration() {
      if (!this.selectedIntegrationId) {
        this.integrationInvalid = true;
        return;
      }

      if (this.selectedSourceType === "xml") {
        this.step = "file";
        return;
      }

      this.$emit("select", {
        type: this.selectedDocumentType,
        integrationId: this.selectedIntegrationId,
        sourceType: this.selectedSourceType,
        sourceOrderId: this.selectedSourceType === "order" ? this.sourceOrderId : null,
      });
    },

    selectIntegrationOption(integrationId) {
      this.selectedIntegrationId = integrationId;
      this.handleIntegrationChange();
      this.selectIntegration();
    },

    async selectSource(source) {
      if (source.disabled) return;

      if (!this.integrationStore.integrations.length) {
        await this.integrationStore.fetchIntegrations();
      }

      this.selectedSourceType = source.key;
      this.integrationInvalid = false;
      this.xmlFileInvalid = false;
      this.step = "integration";
    },

    goBack() {
      if (this.step === "file") {
        this.step = "integration";
        return;
      }

      if (this.step === "integration") {
        this.step = "source";
        return;
      }

      if (this.step === "source" && !this.initialDocumentType) {
        this.step = "document";
      }
    },

    handleIntegrationChange() {
      this.integrationInvalid = false;
    },

    integrationActionLabel(integrationId) {
      if (String(this.selectedIntegrationId) !== String(integrationId)) return "Выбрать";

      return this.selectedSourceType === "xml" ? "Продолжить" : "Создать";
    },

    openXmlFilePicker() {
      this.$refs.xmlFileInput?.click();
    },

    async handleXmlFileChange(event) {
      const [file] = Array.from(event.target.files || []);
      this.xmlFile = file || null;
      this.xmlFileInvalid = false;
      this.xmlFileBase64 = file ? await this.fileToBase64(file) : "";
    },

    submitXmlFile() {
      if (!this.xmlFileBase64) {
        this.xmlFileInvalid = true;
        return;
      }

      this.$emit("select", {
        type: this.selectedDocumentType,
        integrationId: this.selectedIntegrationId,
        sourceType: "xml",
        sourceOrderId: null,
        xmlFileBase64: this.xmlFileBase64,
        xmlFilename: this.xmlFile?.name || null,
      });
    },

    async fileToBase64(file) {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = "";
      const chunkSize = 0x8000;

      for (let index = 0; index < bytes.length; index += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
      }

      return window.btoa(binary);
    },

    formatFileSize(size) {
      if (!size) return "0 КБ";
      if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} КБ`;

      return `${(size / 1024 / 1024).toFixed(1)} МБ`;
    },

    integrationTitle(integration) {
      return integration.name || integration.org_name || "Интеграция Saby";
    },

    integrationRecommendationTitle(integration) {
      if (integration.org_name) return integration.org_name;
      if (integration.org_inn) return `ИНН ${integration.org_inn}`;

      return this.integrationTitle(integration);
    },

    integrationDescription(integration) {
      return [
        integration.org_inn ? `ИНН ${integration.org_inn}` : null,
        integration.org_name,
      ].filter(Boolean).join(" · ");
    },

    stepClass(step) {
      return {
        "create-document-modal__step--active": this.step === step,
        "create-document-modal__step--complete": this.stepComplete(step),
      };
    },

    stepComplete(step) {
      if (step === "document") return ["source", "integration", "file"].includes(this.step);
      if (step === "source") return ["integration", "file"].includes(this.step);
      if (step === "integration") return this.step === "file";
      return false;
    },
  },
};
</script>

<style scoped>
.create-document-modal {
  max-width: 620px;
}

.create-document-modal__stepper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
  margin: 14px 0 16px;
}

.create-document-modal__step {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 12px;
  overflow: hidden;
  color: #6b7280;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.create-document-modal__step:not(:last-child)::after {
  position: absolute;
  top: 50%;
  right: -13px;
  z-index: 1;
  width: 13px;
  height: 1px;
  content: "";
  background: #d1d5db;
}

.create-document-modal__step-index {
  display: inline-flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #6b7280;
  font-size: 13px;
  font-weight: 700;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 50%;
}

.create-document-modal__step-text {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.create-document-modal__step-title {
  color: #374151;
  font-size: 14px;
  font-weight: 700;
}

.create-document-modal__step-description {
  font-size: 12px;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.create-document-modal__step--active {
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #93c5fd;
}

.create-document-modal__step--active .create-document-modal__step-index,
.create-document-modal__step--complete .create-document-modal__step-index {
  color: #ffffff;
  background: #2563eb;
  border-color: #2563eb;
}

.create-document-modal__step--complete {
  color: #2563eb;
  border-color: #bfdbfe;
}

.create-document-modal__subtitle {
  max-width: 480px;
  margin-bottom: 20px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.45;
}

.create-document-modal__hint {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  color: #065f46;
  font-size: 13px;
  line-height: 1.4;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
}

.create-document-modal__hint-icon {
  flex: 0 0 auto;
  margin-top: 1px;
}

.create-document-modal__hint strong,
.create-document-modal__hint span span {
  display: block;
}

.create-document-modal__hint strong {
  margin-bottom: 2px;
  color: #064e3b;
}

.create-document-modal__error,
.create-document-modal__empty {
  padding: 10px 12px;
  color: #b91c1c;
  font-size: 13px;
  line-height: 1.4;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

.create-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.create-document-modal__file-step {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.create-document-modal__file-input {
  display: none;
}

.create-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  width: 100%;
  min-height: 108px;
  padding: 18px;
  color: inherit;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease;
}

.create-option:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
}

.create-option--selected {
  background: #eff6ff;
  border-color: #2563eb;
}

.create-option--disabled {
  color: #9ca3af;
  background: #f9fafb;
  border-color: #e5e7eb;
  opacity: 0.64;
  cursor: not-allowed;
}

.create-option--disabled:hover {
  background: #f9fafb;
  border-color: #e5e7eb;
  box-shadow: none;
}

.create-option--disabled .create-option__title,
.create-option--disabled .create-option__description {
  color: #9ca3af;
}

.create-option--disabled .create-option__action {
  color: #6b7280;
  background: #e5e7eb;
}

.create-option--file {
  min-height: 132px;
}

.create-option__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.create-option__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.create-option__title {
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

.create-option__badge {
  flex: 0 0 auto;
  padding: 2px 8px;
  color: #047857;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
}

.create-option__description {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.45;
}

.create-option__action {
  flex: 0 0 auto;
  padding: 8px 12px;
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  background: #eff6ff;
  border-radius: 6px;
}

@media (max-width: 640px) {
  .create-option {
    gap: 14px;
  }

  .create-option {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
